import { action, makeObservable, observable } from 'mobx';
import { InputState } from '../../components/input/input.state';
import { ModalService } from '../../components/modal/modal.service';
import { CSS as ModalCSS, MDL as ModalMDL } from '../../components/modal/modal.style';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { addStyles, assetPath, DefaultTheme, rgba } from '../../components/theme';
import { BLACK } from '../../components/theme.palette';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Logger } from '../../services/log.service';
import { captureMap, type CaptureRegion, printFrameRegion, waitForRender } from './map.print.capture';
import {
	buildAndDownloadPdf,
	MAP_AREA_ASPECT,
	MAP_AREA_ASPECT_NO_BAND,
	type MapPrintElements,
	type MapPrintPage,
} from './map.print.pdf';

const LOGO_ASSET_PATH = '/images/ike_logo.svg';

// Cap the note so it fits the PDF's note panel without overflowing it.
const NOTE_MAX_LENGTH = 250;

// De-emphasized text (hints, optional label, counter); reuses the theme's secondary-text token.
const SECONDARY_TEXT = DefaultTheme.font.color.supporting;

const CSS = {
	text: 'c-MapPrintModal__text',
	includesTitle: 'c-MapPrintModal__includesTitle',
	checkList: 'c-MapPrintModal__checkList',
	checkRow: 'c-MapPrintModal__checkRow',
	noteLabel: 'c-MapPrintModal__noteLabel',
	noteBox: 'c-MapPrintModal__noteBox',
	noteTextarea: 'c-MapPrintModal__noteTextarea',
	counter: 'c-MapPrintModal__counter',
};

addStyles({
	[CSS.text]: { margin: `0 0 ${DefaultTheme.space(3)}` },
	[CSS.includesTitle]: { fontWeight: 'bold', margin: `0 0 ${DefaultTheme.space(2)}` },
	[CSS.checkList]: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: `${DefaultTheme.space(1)} ${DefaultTheme.space(4)}`,
		margin: `0 0 ${DefaultTheme.space(4)}`,
	},
	[CSS.checkRow]: { display: 'flex', alignItems: 'center', gap: DefaultTheme.space(2), cursor: 'pointer' },
	[CSS.noteLabel]: { fontWeight: 'bold', margin: `0 0 ${DefaultTheme.space(2)}` },
	[CSS.noteBox]: {
		border: `1px solid ${rgba(BLACK, 0.23)}`,
		borderRadius: '4px',
		padding: DefaultTheme.space(2),
	},
	[CSS.noteTextarea]: {
		width: '100%',
		border: 'none',
		outline: 'none',
		resize: 'none',
		background: 'transparent',
		color: DefaultTheme.font.color.supporting,
		font: 'inherit',
	},
	[CSS.counter]: {
		textAlign: 'right',
		color: SECONDARY_TEXT,
		fontSize: '0.85em',
		margin: `${DefaultTheme.space(1)} 0 0`,
	},
});

export interface MapPrintControllerOptions {
	getDepartmentName: () => string;
	getJobName: () => string;
	/**
	 * Put the map into its "ready to capture" state (set collection and vector label visibility) and
	 * return a function that restores the previous state once the capture is done.
	 */
	prepareCapture?: (showCollectionLabels: boolean, showVectorLabels: boolean) => () => void;
	/** Notified when framing starts (true) and ends (false), so the host can lock map interactions. */
	onFramingChange?: (active: boolean) => void;
}

const ELEMENT_OPTIONS: { key: keyof MapPrintElements; label: T }[] = [
	{ key: 'deptJob', label: T.TOPO_PRINT_INCLUDES_DEPT_JOB },
	{ key: 'northArrow', label: T.TOPO_PRINT_INCLUDES_NORTH_ARROW },
	{ key: 'scaleBar', label: T.TOPO_PRINT_INCLUDES_SCALE_BAR },
	{ key: 'legend', label: T.TOPO_PRINT_INCLUDES_LEGEND },
	{ key: 'collectionLabels', label: T.TOPO_PRINT_INCLUDES_COLLECTION_LABELS },
	{ key: 'vectorLabels', label: T.TOPO_PRINT_INCLUDES_VECTOR_LABELS },
	{ key: 'notes', label: T.TOPO_PRINT_INCLUDES_NOTES },
];

function defaultElements(): MapPrintElements {
	return {
		northArrow: true,
		scaleBar: true,
		legend: true,
		collectionLabels: true,
		vectorLabels: true,
		deptJob: true,
		notes: true,
	};
}

/**
 * Drives the "Print Map" flow for Map Based Review Mode: opens the print modal, collects an optional
 * note, lets the user frame the print area on the map, then captures it and generates the PDF export.
 */
export class MapPrintController {
	private readonly note = new InputState(T.TOPO_PRINT_NOTE_LABEL);
	private logoSvg: string | null = null;

	/** True while the user is positioning the print frame over the map. */
	@observable isFraming = false;
	// True while a frame is being rasterized; the toolbar shows a spinner since html2canvas blocks the
	// main thread for a few seconds and the UI would otherwise look frozen.
	@observable private capturing = false;
	// Bumped on resize so the reactive frame getter re-runs (see frameRegion).
	@observable private resizeTick = 0;

	private pendingNote = '';
	// Which elements to include in the export; toggled via the modal checkboxes, reset each open().
	// Observable so the modal (rendered under a MobX autorun) re-renders when a checkbox flips.
	@observable private elements: MapPrintElements = defaultElements();
	// Frames banked via "Add page" during framing; one PDF page each. Observable so the toolbar's page
	// counter re-renders as pages are added. Reset on open/cancel.
	@observable private pages: MapPrintPage[] = [];
	private readonly onResize = () => this.bumpResizeTick();
	private readonly onKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			this.cancelFraming();
		}
	};

	constructor(private readonly opts: MapPrintControllerOptions) {
		makeObservable(this);
	}

	/** Number of frames banked via "Add page" so far; drives the toolbar's page counter. */
	get pageCount(): number {
		return this.pages.length;
	}

	/** True while a frame capture is in progress; the toolbar reflects this with a busy/spinner state. */
	get isCapturing(): boolean {
		return this.capturing;
	}

	open(): void {
		this.note.reset();
		this.note.placeholder = Translate(T.TOPO_PRINT_NOTE_PLACEHOLDER);
		this.resetElements();
		this.clearPages();

		void ModalService.open({
			title: T.TOPO_PRINT_MAP,
			content: () => this.viewContent(),
			actions: () => this.viewActions(),
			canClose: () => true,
		}).then((response) => {
			if (response.ok) {
				this.enterFraming(this.note.value ?? '');
			}
		});
	}

	cancel(): void {
		if (ModalService.isOpen) {
			ModalService.close(false);
		}
		if (this.isFraming) {
			this.cancelFraming();
		}
	}

	/** The print frame / capture crop, in CSS pixels relative to the map wrapper. The aspect drops the
	 *  bottom band when neither legend nor note is included, so the map fills the page. */
	get frameRegion(): CaptureRegion | null {
		void this.resizeTick; // re-run on resize
		const bandPresent = this.elements.legend || (this.elements.notes && this.pendingNote.trim().length > 0);
		return printFrameRegion(bandPresent ? MAP_AREA_ASPECT : MAP_AREA_ASPECT_NO_BAND);
	}

	/** Capture the current frame and bank it as a page, staying in framing to compose the next one. */
	@action.bound addPage(): void {
		if (!this.isFraming || this.capturing) {
			return;
		}
		void this.runAddPage();
	}

	/** Finish: export the banked pages, or — if none were banked — a one-shot capture of the current
	 *  frame. Either way exits framing once the capture is taken. */
	@action.bound exportFraming(): void {
		if (!this.isFraming || this.capturing) {
			return;
		}
		void this.runExport();
	}

	@action.bound cancelFraming(): void {
		if (!this.isFraming) {
			return;
		}
		this.exitFraming();
		this.clearPages();
	}

	@action private enterFraming(note: string): void {
		this.pendingNote = note;
		this.isFraming = true;
		this.opts.onFramingChange?.(true);
		window.addEventListener('keydown', this.onKeyDown);
		window.addEventListener('resize', this.onResize);
	}

	@action private exitFraming(): void {
		this.isFraming = false;
		this.opts.onFramingChange?.(false);
		window.removeEventListener('keydown', this.onKeyDown);
		window.removeEventListener('resize', this.onResize);
	}

	@action private bumpResizeTick(): void {
		this.resizeTick++;
	}

	private async runAddPage(): Promise<void> {
		try {
			const page = await this.captureCurrentFrame();
			this.bankPage(page);
			NotificationService.info({ key: T.TOPO_PRINT_PAGE_ADDED, attrs: { count: this.pages.length } });
		} catch (error) {
			Logger.error({ err: error }, 'Failed to capture map page');
			NotificationService.error({ key: T.TOPO_PRINT_ERROR });
		}
	}

	private async runExport(): Promise<void> {
		NotificationService.info({ key: T.TOPO_PRINT_CAPTURING });
		try {
			// Use the banked pages; if none were added, capture the current frame as a single page so the
			// quick one-shot export still works without an explicit "Add page".
			const pages = this.pages.length > 0 ? this.pages.slice() : [await this.captureCurrentFrame()];
			// Hide the overlay before building so a stray redraw can't leave it on screen mid-download.
			this.exitFraming();
			const logoSvg = await this.loadLogoSvg();
			buildAndDownloadPdf({
				pages,
				departmentName: this.opts.getDepartmentName(),
				jobName: this.opts.getJobName(),
				note: this.pendingNote,
				logoSvg,
				elements: this.elements,
			});
		} catch (error) {
			Logger.error({ err: error }, 'Failed to export map to PDF');
			NotificationService.error({ key: T.TOPO_PRINT_ERROR });
		} finally {
			this.clearPages();
		}
	}

	/** Snapshot the current frame into a PDF-ready page. Forces the chosen collection/vector label
	 *  visibility around the capture so every page is consistent regardless of the live toggle. */
	private async captureCurrentFrame(): Promise<MapPrintPage> {
		const region = this.frameRegion;
		const restoreCaptureState =
			this.opts.prepareCapture?.(this.elements.collectionLabels, this.elements.vectorLabels) ?? (() => {});
		this.setCapturing(true);
		try {
			// Let the busy state paint before html2canvas seizes the main thread for the next few seconds.
			await waitForRender();
			const { canvas, metersPerImagePixel, attribution } = await captureMap(region ?? undefined);
			return {
				imageDataUrl: canvas.toDataURL('image/jpeg', 0.92),
				imageWidth: canvas.width,
				imageHeight: canvas.height,
				metersPerImagePixel,
				attribution,
			};
		} finally {
			restoreCaptureState();
			this.setCapturing(false);
		}
	}

	@action private setCapturing(value: boolean): void {
		this.capturing = value;
	}

	@action private bankPage(page: MapPrintPage): void {
		this.pages.push(page);
	}

	@action private clearPages(): void {
		this.pages = [];
	}

	/** Fetch and cache the ikeGPS logo SVG markup. Returns an empty string if it can't be loaded,
	 *  so a missing logo never blocks the export. */
	private async loadLogoSvg(): Promise<string> {
		if (this.logoSvg != null) {
			return this.logoSvg;
		}
		try {
			const response = await fetch(assetPath(LOGO_ASSET_PATH));
			this.logoSvg = response.ok ? await response.text() : '';
		} catch (error) {
			Logger.error({ err: error }, 'Failed to load map print logo');
			this.logoSvg = '';
		}
		return this.logoSvg;
	}

	private viewContent(): MithrilVNode {
		return m('div', [
			m('p', { className: CSS.text }, Translate(T.TOPO_PRINT_MODAL_EXTENT_INFO)),
			m('p', { className: CSS.includesTitle }, Translate(T.TOPO_PRINT_INCLUDES_TITLE)),
			m(
				'div',
				{ className: CSS.checkList },
				ELEMENT_OPTIONS.map((option) =>
					m('label', { className: CSS.checkRow }, [
						m('input', {
							type: 'checkbox',
							checked: this.elements[option.key],
							onchange: () => this.toggleElement(option.key),
						}),
						Translate(option.label),
					])
				)
			),
			this.noteField(),
		]);
	}

	@action.bound private toggleElement(key: keyof MapPrintElements): void {
		this.elements[key] = !this.elements[key];
	}

	@action private resetElements(): void {
		this.elements = defaultElements();
	}

	private noteField(): MithrilVNode {
		// Always rendered (just disabled when the Notes checkbox is off) so toggling doesn't shift the
		// modal's vnode tree — Mithril 1.x patches by index and would mis-patch the textarea otherwise.
		const enabled = this.elements.notes;
		const used = (this.note.inputValue ?? '').length;
		return m('div', { style: { opacity: enabled ? '1' : '0.5' } }, [
			m('div', { className: CSS.noteLabel }, Translate(T.TOPO_PRINT_NOTE_LABEL)),
			m(
				'div',
				{ className: CSS.noteBox },
				m('textarea', {
					className: CSS.noteTextarea,
					rows: 6,
					maxlength: NOTE_MAX_LENGTH,
					disabled: !enabled,
					placeholder: this.note.placeholder,
					value: this.note.inputValue ?? '',
					oninput: (event: InputEvent) => {
						if (event.target instanceof HTMLTextAreaElement) {
							this.note.setInputValue(event.target.value);
						}
					},
				})
			),
			m('div', { className: CSS.counter }, `${used} / ${NOTE_MAX_LENGTH}`),
		]);
	}

	private viewActions(): MithrilVNode {
		return m('div', [
			m(
				'button',
				{ className: ModalMDL.button, type: 'button', onclick: () => ModalService.close(false) },
				Translate(T.CANCEL)
			),
			m(
				'button',
				{
					className: `${ModalMDL.button} ${ModalCSS.action.ok}`,
					type: 'submit',
					onclick: () => ModalService.close(true),
				},
				Translate(T.TOPO_PRINT_CHOOSE_AREA)
			),
		]);
	}
}
